import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../../lib/config";
import Icons from "../../lib/mapIcons";
import AnimatedPolyline from "./AnimatedPolyline";

export default function Map({
  center = [51.5, 10.4],
  zoom = 7,
  centers = [],
  markers = [], // -> with fly animation to fit in viewport
  additionalMarkers = [], // -> without animation
  polylines = [],
  animatedPolylines = [],
  paddingBottomRight = [150, 0],
  paddingTopLeft = [0, 150],
}) {
  const router = useRouter();
  const leafletMap = useRef();
  const centerMarkers = useRef([]);
  const centerMarkersGroup = useRef();
  const markersGroup = useRef();
  const additionalMarkersGroup = useRef();
  const polylinesGroup = useRef();
  const featureGroup = useRef();
  const popup = useRef();

  const mapOptions = {
    center,
    zoom,
    doubleClickZoom: false,
    zoomControl: false,
    maxZoom: 9,
    minZoom: 2,
    attributionControl: false,
  };

  const initMap = () => {
    leafletMap.current = L.map("map", mapOptions);

    L.tileLayer(config.tileServerUrl, {
      id: config.tileServerId,
      accessToken: config.tileServerAccessToken,
    }).addTo(leafletMap.current);

    // const attribution = L.control.attribution({ position: "bottomleft" });
    // attribution.addAttribution(
    //   '<a href="https://github.com/maptiler/tileserver-gl/blob/master/LICENSE.md" target="_blank">tileserver-gl</a>',
    // );
    // attribution.addTo(leafletMap.current);

    L.control.zoom({ position: "bottomleft" }).addTo(leafletMap.current);

    featureGroup.current = L.featureGroup().addTo(leafletMap.current);
  };

  const updateFeatureGroupLayer = (container, featureGroup) => {
    removeFeatureGroupLayer(container.current);
    container.current = featureGroup;
    addFeatureGroupLayer(container.current);
  };

  const removeFeatureGroupLayer = (layer) => {
    if (layer) featureGroup.current.removeLayer(layer);
  };

  const addFeatureGroupLayer = (layer) => {
    if (layer) featureGroup.current.addLayer(layer);
  };

  const getMarkerGroup = (markerData) => {
    const newMarkers = markerData.map((data) => {
      const icon = Icons[data.icon](data.color);
      const latLng = [data.latitude, data.longitude];
      const zIndexOffset = () => {
        if (data.zIndexOffset) return data.zIndexOffset;
        if (data.icon === "house") return 80;
        if (
          data.icon === "center" ||
          data.icon === "subCenter" ||
          data.icon === "frankfurt"
        ) {
          return 70;
        }
        return 0;
      };

      const marker = L.marker(latLng, {
        title: data.title,
        icon,
        zIndexOffset: zIndexOffset(),
      });

      if (data.href) {
        marker.on("click", () => router.push(data.href));
      }

      if (data.title) {
        marker.bindTooltip(data.title, {
          permanent: true,
          direction: "top",
        });
      }

      if (data.popupContent) {
        if (popup.current) {
          popup.current.remove();
        }

        const width = 285;

        popup.current = L.popup({
          className: "popup",
          maxWidth: width,
          minWidth: width,
          offset: L.point(-width / 2, 0),
          closeOnClick: false,
        })
          .setLatLng(latLng)
          .setContent(data.popupContent)
          .addTo(leafletMap.current)
          .openPopup();
      }

      return marker;
    });

    return L.featureGroup(newMarkers);
  };

  const getPolylineGroup = (polylinesData) => {
    if (!polylinesData || polylinesData.length < 1) return;

    const newPolylines = polylinesData.map((data) => {
      const { className, coords, color } = data;

      return L.polyline(coords, {
        className,
        color,
        lineCap: "round",
      });
    });

    return L.featureGroup(newPolylines);
  };

  const updateCenterMarkers = () => {
    centerMarkers.current = centers.map(
      ({ title, slug, latitude, longitude, type, href }) => {
        return {
          title,
          latitude,
          longitude,
          href,
          icon:
            slug === "frankfurt-am-main"
              ? "frankfurt"
              : type.value === "sub_center"
              ? "subCenter"
              : "center",
        };
      },
    );
  };

  useEffect(() => {
    if (leafletMap.current) return;
    initMap();
  }, [leafletMap]);

  useEffect(() => {
    if (!leafletMap.current) return;

    updateFeatureGroupLayer(polylinesGroup, getPolylineGroup(polylines));
  }, [leafletMap, polylines]);

  useEffect(() => {
    if (!leafletMap.current) return;

    updateFeatureGroupLayer(markersGroup, getMarkerGroup(markers));

    if (markers.length > 0) {
      leafletMap.current.flyToBounds(markersGroup.current.getBounds(), {
        paddingBottomRight,
        paddingTopLeft,
        maxZoom: 7,
      });
    }
  }, [leafletMap, markers]);

  useEffect(() => {
    if (!leafletMap.current) return;

    updateFeatureGroupLayer(additionalMarkersGroup, getMarkerGroup(additionalMarkers));
  }, [leafletMap, additionalMarkers]);

  useEffect(() => {
    if (!centers) return;

    updateCenterMarkers();
    updateFeatureGroupLayer(centerMarkersGroup, getMarkerGroup(centerMarkers.current));

    // fit centers in viewport when there are no markers
    if (markers.length < 1 && centers.length > 0) {
      leafletMap.current.flyToBounds(centerMarkersGroup.current.getBounds(), {
        paddingBottomRight,
        paddingTopLeft,
        maxZoom: 7,
      });
    }
  }, [centers]);

  return (
    <div
      id="map"
      className="fixed top-0 left-0 h-full w-full"
      style={{ background: "#00454C" }}
    >
      {animatedPolylines.length > 0 && leafletMap.current
        ? animatedPolylines.map((line) => {
            const { className, coords, color, index } = line;
            return (
              <AnimatedPolyline
                key={index}
                options={{ className, color }}
                coords={coords}
                map={leafletMap.current}
              />
            );
          })
        : null}
    </div>
  );
}
